"use client";

import { useEffect, useState } from "react";

/** A single change record emitted by an opportunity workflow. */
export type OppChangeRecord = {
  id: string;
  /** Grouping label (e.g. "Assumptions", "Organization Details"). */
  category: string;
  /** Human-readable description of the change. */
  description: string;
};

export interface OppChangesModalProps {
  changes: OppChangeRecord[];
  onRevert: () => void | Promise<void>;
  onClose: () => void;
  onConfirm?: () => void | Promise<void>;
  revertLabel?: string;
  confirmLabel?: string;
  busy?: boolean;
  error?: string | null;
}

const CLS_FOOT_BTN = [
  "inline-flex items-center justify-center",
  "h-8 px-3.5 rounded-(--radius-field)",
  "text-sm font-medium cursor-pointer",
  "transition-[background,color,border-color] duration-120",
].join(" ");

/**
 * Opportunity-scoped unsaved-changes modal. Groups change records by
 * `category` and offers a two-step "Revert all" confirm flow. Closes on
 * Escape or overlay click.
 */
export function OppChangesModal({
  changes,
  onRevert,
  onClose,
  onConfirm,
  revertLabel = "Revert all",
  confirmLabel = "Confirm & save",
  busy = false,
  error = null,
}: OppChangesModalProps) {
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const grouped = new Map<string, OppChangeRecord[]>();
  for (const c of changes) {
    const list = grouped.get(c.category) ?? [];
    list.push(c);
    grouped.set(c.category, list);
  }

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: overlay click-to-close; Escape handled above
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-[color-mix(in_oklab,var(--brand-navy)_30%,transparent)]"
      role="dialog"
      aria-modal="true"
      aria-label="Unsaved changes"
      onClick={onClose}
    >
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: inner panel stops propagation only */}
      <div
        role="document"
        className="w-full max-w-[540px] max-h-[80vh] flex flex-col overflow-hidden rounded-(--radius-container) bg-(--bg-white) shadow-(--shadow-lg)"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-4 px-5 pt-4.5 pb-3.5 border-b border-(--border-subtle)">
          <div>
            <h2 className="font-(family-name:--font-display) text-sm font-bold tracking-[-0.015em] text-(--fg-primary) m-0">
              Unsaved changes
            </h2>
            <p className="text-[12.5px] text-(--fg-muted) mt-1 mb-0">
              {changes.length} change{changes.length === 1 ? "" : "s"} since
              last save
            </p>
          </div>
          <button
            type="button"
            className="inline-flex items-center justify-center w-7 h-7 rounded-[3px] text-(--fg-muted) cursor-pointer transition-[background,color] duration-100 hover:bg-(--bg-hover) hover:text-(--fg-primary)"
            onClick={onClose}
            aria-label="Close"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden={true}
            >
              <path d="M6 6l12 12M18 6l-12 12" />
            </svg>
          </button>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 pt-1.5 pb-3.5">
          {changes.length === 0 ? (
            <p className="my-6 text-center text-[13px] text-(--fg-muted)">
              No changes to show.
            </p>
          ) : (
            [...grouped.entries()].map(([category, list]) => (
              <section
                key={category}
                className="py-3.5 border-b border-(--border-subtle) last:border-b-0"
              >
                <h3 className="font-(family-name:--font-body) text-[11px] font-semibold uppercase tracking-[0.08em] text-(--fg-muted) mt-0 mb-2">
                  {category}
                </h3>
                <ul className="list-none p-0 m-0 flex flex-col gap-1">
                  {list.map((c) => (
                    <li
                      key={c.id}
                      className="relative pl-3.5 text-[13px] leading-normal text-(--fg-primary) before:content-[''] before:absolute before:left-0 before:top-[9px] before:w-1 before:h-1 before:rounded-[3px] before:bg-(--fg-subtle)"
                    >
                      {c.description}
                    </li>
                  ))}
                </ul>
              </section>
            ))
          )}
        </div>

        {error && (
          <div
            role="alert"
            className="border-t border-(--border-subtle) bg-(--status-error-bg) px-5 py-2.5 text-[12.5px] text-(--status-error-text)"
          >
            {error}
          </div>
        )}

        <footer className="flex items-center justify-between gap-3 px-5 py-3.5 border-t border-(--border-subtle) bg-(--bg-white)">
          {confirming ? (
            <>
              <span className="flex-1 min-w-0 text-[12.5px] text-(--fg-secondary)">
                Discard {changes.length} change
                {changes.length === 1 ? "" : "s"}? This cannot be undone.
              </span>
              <div className="inline-flex gap-2">
                <button
                  type="button"
                  className={`${CLS_FOOT_BTN} text-(--fg-secondary) bg-(--bg-canvas) hover:bg-(--bg-focused) hover:text-(--fg-primary)`}
                  onClick={() => setConfirming(false)}
                  disabled={busy}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`${CLS_FOOT_BTN} bg-(--status-error) text-(--fg-inverse) hover:bg-(--error-700) disabled:cursor-default disabled:opacity-60`}
                  onClick={() => void onRevert()}
                  disabled={busy}
                >
                  {revertLabel}
                </button>
              </div>
            </>
          ) : onConfirm ? (
            <>
              <button
                type="button"
                className={`${CLS_FOOT_BTN} bg-transparent text-(--status-error-text) hover:bg-(--status-error-bg) disabled:text-(--fg-subtle) disabled:cursor-default`}
                onClick={() => setConfirming(true)}
                disabled={busy || changes.length === 0}
              >
                {revertLabel}
              </button>
              <button
                type="button"
                className={`${CLS_FOOT_BTN} bg-(--brand-navy) text-(--fg-inverse) hover:bg-(--steel-110) disabled:cursor-default disabled:opacity-60`}
                onClick={() => void onConfirm()}
                disabled={busy || changes.length === 0}
              >
                {busy ? "Saving…" : confirmLabel}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className={`${CLS_FOOT_BTN} bg-transparent text-(--status-error-text) hover:bg-(--status-error-bg) disabled:text-(--fg-subtle) disabled:cursor-default`}
                onClick={() => setConfirming(true)}
                disabled={changes.length === 0}
              >
                Revert all
              </button>
              <button
                type="button"
                className={`${CLS_FOOT_BTN} bg-(--brand-navy) text-(--fg-inverse) hover:bg-(--steel-110)`}
                onClick={onClose}
              >
                Done
              </button>
            </>
          )}
        </footer>
      </div>
    </div>
  );
}
